import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import {
  RecurrenceChange,
  RecurrenceFuture,
  RecurrenceImpact,
  RecurrencePattern,
  RecurrenceView,
} from "../model/recurrence";

/**
 * Het terugkeerpatroon van een ticket: opvragen, instellen en beeindigen.
 *
 * Aangesproken per ticket, niet per patroon, omdat de schermen zo werken: de
 * gebruiker kijkt naar een ticket en wil weten hoe het terugkomt.
 */
@Injectable({
  providedIn: "root",
})
export class RecurrenceService {
  private apiUrl: string;

  /** Het patroon van het ticket dat nu open staat, of null. */
  public currentRecurrence: BehaviorSubject<RecurrenceView | null>;

  /** Wat een annulering zou raken, opgehaald vlak voor de bevestigingsdialoog. */
  public currentImpact: BehaviorSubject<RecurrenceImpact | null>;

  /**
   * Welke tickets uit de getoonde lijst bij een reeks horen, als ticket-id
   * naar patroon-id. Gevuld met een aanroep per pagina, niet per rij.
   */
  public recurringTickets: BehaviorSubject<Map<number, number>>;

  /**
   * De pagina waarvoor de markeringen al zijn opgehaald. De lijst wordt door
   * meerdere abonnementen opnieuw opgebouwd, en zonder dit werd dezelfde vraag
   * bij elke doorloop opnieuw aan de server gesteld.
   */
  private loadedForTickets: string;

  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper
  ) {
    this.currentRecurrence = new BehaviorSubject<RecurrenceView | null>(null);
    this.currentImpact = new BehaviorSubject<RecurrenceImpact | null>(null);
    this.recurringTickets = new BehaviorSubject<Map<number, number>>(new Map());
    this.apiUrl = `${env.apiBaseUrl}recurrence/`;
    this.loadedForTickets = "";
  }

  private authHeaders(token: string) {
    return {
      Authorization: "Bearer " + token,
      "Content-type": "application/json",
    };
  }

  /**
   * Haalt het terugkeerpatroon van een ticket op.
   *
   * Een ticket zonder patroon levert 404 op; dat is geen fout maar het normale
   * antwoord voor een gewoon ticket, dus het wordt stil afgehandeld.
   */
  public byTicket(token: string, ticketId: number): Observable<Messages> {
    const url = `${this.apiUrl}by-ticket/${ticketId}`;

    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(url, {
          headers: this.authHeaders(token),
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveView(response, observer),
          (error) => this.receiveView(error, observer)
        );
    });
  }

  /**
   * Stelt het patroon van een ticket in, of vervangt het bestaande.
   *
   * Een nieuw patroon hangt het ticket meteen als eerste aflevering aan de
   * reeks en vult het venster erachter, dus na deze aanroep kunnen er nieuwe
   * tickets in de lijst staan.
   */
  public save(
    token: string,
    ticketId: number,
    pattern: RecurrencePattern,
    onChange: RecurrenceChange = "AFTER_WORKED"
  ): Observable<Messages> {
    // onChange only counts when the ticket already has a pattern; for a new
    // pattern there is no future to do anything with and the server ignores it
    const url = `${this.apiUrl}by-ticket/${ticketId}?onChange=${onChange}`;

    // the markers in the list are out of date after this
    this.loadedForTickets = "";

    return new Observable<Messages>((observer) => {
      this.http
        .put<any>(url, pattern, {
          headers: this.authHeaders(token),
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveView(response, observer),
          (error) => this.receiveView(error, observer)
        );
    });
  }

  /**
   * Wat het wijzigen van de regel zou raken, zonder iets op te slaan.
   *
   * Een POST, want de voorgestelde regel gaat mee in de body. Het antwoord
   * noemt elk ticket dat nog voor de boeg staat, met of eraan gewerkt is en of
   * de nieuwe regel die datum nog plant, plus waar het genereren weer oppakt.
   */
  public impactOfChange(
    token: string,
    ticketId: number,
    pattern: RecurrencePattern
  ): Observable<Messages> {
    const url = `${this.apiUrl}by-ticket/${ticketId}/impact-of-change`;

    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(url, pattern, {
          headers: this.authHeaders(token),
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveImpact(response, observer),
          (error) => this.receiveImpact(error, observer)
        );
    });
  }

  /** Wat het beeindigen van deze reeks zou raken. */
  public impact(token: string, ticketId: number): Observable<Messages> {
    const url = `${this.apiUrl}by-ticket/${ticketId}/impact`;

    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(url, {
          headers: this.authHeaders(token),
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveImpact(response, observer),
          (error) => this.receiveImpact(error, observer)
        );
    });
  }

  /**
   * Beeindigt de reeks waar dit ticket bij hoort.
   *
   * @param future 'delete' verwijdert de tickets die nog komen, 'keep' laat ze
   * staan als losse tickets
   */
  public cancel(
    token: string,
    ticketId: number,
    future: RecurrenceFuture
  ): Observable<Messages> {
    const url = `${this.apiUrl}by-ticket/${ticketId}?future=${future}`;

    // the markers in the list are out of date after this
    this.loadedForTickets = "";

    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: this.authHeaders(token),
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveCancel(response, observer),
          (error) => this.receiveCancel(error, observer)
        );
    });
  }

  /**
   * Zoekt in een keer op welke tickets van een lijst bij een reeks horen,
   * zodat de rijen een markering kunnen tonen zonder aanroep per rij.
   *
   * @param ticketIds de tickets van de getoonde pagina
   */
  public loadForTickets(token: string, ticketIds: number[]): void {
    const wanted = (ticketIds ?? []).filter((id) => !!id && id > 0);

    if (wanted.length === 0) {
      this.loadedForTickets = "";
      this.recurringTickets.next(new Map());
      return;
    }
    const key = wanted.join("-");

    if (key === this.loadedForTickets) {
      return;
    }
    this.loadedForTickets = key;

    const url = `${this.apiUrl}by-tickets/${key}`;

    this.http
      .get<any>(url, { headers: this.authHeaders(token), observe: "response" })
      .subscribe(
        (response) => this.receiveRecurring(response),
        () => {
          this.loadedForTickets = "";
          this.recurringTickets.next(new Map());
        }
      );
  }

  /** Of dit ticket deel uitmaakt van een reeks, voor de markering in de lijst. */
  public isRecurring(ticketId: number | null | undefined): boolean {
    return !!ticketId && this.recurringTickets.value.has(ticketId);
  }

  private receiveRecurring(response: HttpResponseBase): void {
    const found = new Map<number, number>();

    if (response.status === 200) {
      const data = (response as HttpResponse<any>)?.body ?? {};
      for (const key of Object.keys(data)) {
        const ticketId = Number(key);
        if (!isNaN(ticketId)) {
          found.set(ticketId, Number(data[key]));
        }
      }
    }
    this.recurringTickets.next(found);
  }

  /** Legt het patroon van het geopende ticket vast zonder eigen aanroep. */
  public useFromTicketParts(raw: any): void {
    this.currentRecurrence.next(RecurrenceView.from(raw));
  }

  public clear(): void {
    this.currentRecurrence.next(null);
    this.currentImpact.next(null);
  }

  private receiveView(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<any>)?.body;
      this.currentRecurrence.next(RecurrenceView.from(data));
      observer.next(Messages.EMPTY);
    } else if (response.status === 404) {
      // een gewoon ticket zonder terugkeerpatroon
      this.currentRecurrence.next(null);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "unauthorized.access."
        )
      );
    } else if (
      response.status === 400 ||
      response.status === 409 ||
      response.status === 500
    ) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "recurrence",
          "recurrence.save.error"
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "general",
          "an.unexpected.error.has.occurred."
        )
      );
    }
  }

  private receiveImpact(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<any>)?.body;
      this.currentImpact.next(RecurrenceImpact.from(data));
      observer.next(Messages.EMPTY);
    } else if (response.status === 404) {
      // niets aan de hand: dit ticket hoort niet bij een reeks
      this.currentImpact.next(null);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "unauthorized.access."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "recurrence",
          "recurrence.impact.error"
        )
      );
    }
  }

  private receiveCancel(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200 || response.status === 404) {
      this.currentRecurrence.next(null);
      this.currentImpact.next(null);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "unauthorized.access."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "recurrence",
          "recurrence.cancel.error"
        )
      );
    }
  }
}
